import { Compass, FileText, Cloud, AlertTriangle, MapPin, Fuel, Scale, Ruler, ClipboardCheck, Users, HelpCircle } from "lucide-react";

// The 11-point VFR pre-flight briefing checklist.
export const CHECKLIST_POINTS = [
  {
    id: 1,
    title: "Documents Pilote",
    icon: FileText,
    purpose: "Vérifier que le pilote détient tous les documents réglementaires valides pour effectuer le vol.",
    checks: [
      "Licence (PPL/LAPL) valide et qualifications à jour",
      "Certificat médical en cours de validité",
      "Expérience récente : 3 décollages/atterrissages 90 jours (si emport pax)",
      "Carnet de vol / carte d'identité",
      "Aptitude linguistique (FR / anglais) si requise",
    ],
    fields: [
      { key: "licenceExpiry", label: "Validité licence", type: "text" },
      { key: "medicalExpiry", label: "Validité médical", type: "text" },
    ],
    entity: "pilot",
  },
  {
    id: 2,
    title: "Documents Avion",
    icon: Compass,
    purpose: "S'assurer que l'aéronef est en état de vol et que ses documents de bord sont complets et valides.",
    checks: [
      "CDN / CEN (certificat de navigabilité + examen)",
      "Assurance en cours de validité",
      "Licence station radio / carnet de route",
      "Manuel de vol (POH) à bord",
      "Visite prévol effectuée, potentiel maintenance restant",
    ],
    fields: [
      { key: "cdnExpiry", label: "Validité CEN/CDN", type: "text" },
      { key: "maintRemaining", label: "Potentiel restant", type: "text" },
    ],
    entity: "aircraft",
  },
  {
    id: 3,
    title: "Météo",
    icon: Cloud,
    purpose: "Analyser les conditions météo départ, route et arrivée pour confirmer la faisabilité du vol VFR.",
    checks: [
      "METAR / TAF départ, arrivée et dégagement",
      "TEMSI (temps significatif) et WINTEM (vent/tempé altitude)",
      "Plafond et visibilité compatibles VFR",
      "Vent, rafales, composante de travers piste",
      "Coucher du soleil / heure de nuit aéronautique",
    ],
    fields: [
      { key: "metarDep", label: "METAR/TAF départ", type: "textarea" },
      { key: "metarArr", label: "METAR/TAF arrivée", type: "textarea" },
      { key: "wintem", label: "WINTEM (vent/temp)", type: "textarea" },
    ],
    resources: [
      { label: "aviation.meteo.fr", url: "https://aviation.meteo.fr" },
    ],
  },
  {
    id: 4,
    title: "NOTAM / SUP AIP / AZBA",
    icon: AlertTriangle,
    purpose: "Identifier les restrictions d'espace, zones activées et informations aéronautiques temporaires.",
    checks: [
      "NOTAM départ, route et arrivée",
      "SUP AIP applicables",
      "AZBA (zones basse altitude militaire) actives",
      "Zones P/R/D et activités particulières",
      "État des terrains, pistes, balisage",
    ],
    fields: [
      { key: "notam", label: "Synthèse NOTAM", type: "textarea" },
      { key: "supAip", label: "SUP AIP applicables", type: "textarea" },
      { key: "azba", label: "AZBA actives", type: "textarea" },
      { key: "zonesPRD", label: "Zones P/R/D et activités particulières", type: "textarea" },
    ],
    resources: [
      { label: "SOFIA-Briefing", url: "https://sofia-briefing.aviation-civile.gouv.fr" },
    ],
  },
  {
    id: 5,
    title: "Trajet & Alternats",
    icon: MapPin,
    purpose: "Confirmer la route choisie, les points tournants, les altitudes et les terrains de dégagement.",
    checks: [
      "Points tournants identifiables et espacés",
      "Altitudes / niveaux compatibles espaces et relief (sécurité mini)",
      "Terrains de dégagement retenus",
      "Fréquences radio par segment",
      "Calage de la nav sur le log (caps, temps)",
    ],
    fields: [
      { key: "alternates", label: "Terrains de dégagement", type: "text" },
      { key: "frequencies", label: "Fréquences clés", type: "textarea" },
    ],
  },
  {
    id: 6,
    title: "Bilan Carburant",
    icon: Fuel,
    purpose: "Vérifier que le carburant embarqué couvre le trajet, le dégagement et les réserves réglementaires.",
    checks: [
      "Carburant roulage + voyage + dégagement",
      "Réserve finale (30 min jour VFR)",
      "Forfait procédure (départ / arrivée)",
      "Carburant embarqué ≥ minimum réglementaire",
      "Marge positive confirmée",
    ],
    fields: [{ key: "fuelNotes", label: "Remarques carburant", type: "textarea" }],
    linkTab: "carburant",
  },
  {
    id: 7,
    title: "Devis Masse & Centrage",
    icon: Scale,
    purpose: "Calculer la masse et le centre de gravité au départ et à l'arrivée, dans l'enveloppe certifiée.",
    checks: [
      "Masse ≤ MTOW au décollage",
      "Centrage dans l'enveloppe au décollage ET à l'atterrissage",
      "Répartition charge (pax, bagages, carburant)",
      "Moment total cohérent",
    ],
    fields: [{ key: "wbNotes", label: "Remarques masse/centrage", type: "textarea" }],
    linkTab: "centrage",
  },
  {
    id: 8,
    title: "Limitations Opérationnelles",
    icon: Ruler,
    purpose: "Comparer les distances décollage / atterrissage requises aux distances disponibles sur les terrains.",
    checks: [
      "Distance de décollage requise ≤ TORA/TODA",
      "Distance d'atterrissage requise ≤ LDA",
      "Effet altitude/température/pente/état de piste",
      "Marges de sécurité appliquées",
    ],
    fields: [{ key: "limNotes", label: "Remarques limitations", type: "textarea" }],
    linkTab: "limitations",
  },
  {
    id: 9,
    title: "Plan de vol (FPL)",
    icon: ClipboardCheck,
    purpose: "Déterminer si un plan de vol est nécessaire et le préparer / déposer le cas échéant.",
    checks: [
      "FPL requis ? (survol maritime, franchissement frontière, ...)",
      "Renseignements FPL complétés",
      "Heure estimée de départ (EOBT)",
      "Message de départ / clôture prévu",
    ],
    fields: [
      { key: "fplRequired", label: "FPL requis ?", type: "text" },
      { key: "eobt", label: "EOBT", type: "text" },
    ],
  },
  {
    id: 10,
    title: "Briefing Passagers",
    icon: Users,
    purpose: "Informer les passagers de la sécurité, du déroulement et des consignes à bord.",
    checks: [
      "Ceintures, portes, issues de secours",
      "Consignes en cas d'urgence",
      "Interdiction de fumer, effets bagages",
      "Mal de l'air, communication à bord",
      "Rôle éventuel d'un passager",
    ],
    fields: [{ key: "paxNotes", label: "Notes briefing passagers", type: "textarea" }],
  },
  {
    id: 11,
    title: "Questions & Synthèse",
    icon: HelpCircle,
    purpose: "Faire le point final, lever les doutes et prendre la décision GO / NO-GO.",
    checks: [
      "Synthèse des éléments clés",
      "Points d'attention particuliers",
      "Décision GO / NO-GO argumentée",
      "Questions diverses de l'instructeur",
    ],
    fields: [
      { key: "decision", label: "Décision GO / NO-GO", type: "text" },
      { key: "questions", label: "Questions diverses", type: "textarea" },
    ],
  },
];

export const RESOURCE_LINKS = [
  { label: "SOFIA-Briefing (NOTAM/PIB)", url: "https://sofia-briefing.aviation-civile.gouv.fr" },
  { label: "aviation.meteo.fr (METAR/TAF/TEMSI)", url: "https://aviation.meteo.fr" },
  { label: "SIA — AIP France", url: "https://www.sia.aviation-civile.gouv.fr" },
];

// Reference websites. `subs` = default sub-item ids where the link appears.
export const REFERENCE_SITES = [
  { id: "aeroweb", name: "Aeroweb — aviation.meteo.fr", url: "https://aviation.meteo.fr/", usage: "Cartes WINTEM, TEMSI, METAR / TAF, SIGMET (France)", subs: ["3::metarDep", "3::metarArr", "3::wintem"] },
  { id: "metartaf", name: "METAR-TAF.com", url: "https://metar-taf.com/", usage: "METAR et TAF détaillés (monde), visualisation graphique", subs: ["3::metarDep", "3::metarArr"] },
  { id: "sofia", name: "SOFIA-Briefing", url: "https://sofia-briefing.aviation-civile.gouv.fr/", usage: "NOTAM / PIB, SUP AIP, dépôt de plan de vol", subs: ["4::notam", "4::supAip", "9::fplRequired"] },
  { id: "sia", name: "SIA — AIP France", url: "https://www.sia.aviation-civile.gouv.fr/", usage: "AIP, cartes VAC, SUP AIP, cartes aéronautiques", subs: ["4::supAip", "vac"] },
  { id: "azba", name: "AZBA — RTBA", url: "https://www.sia.aviation-civile.gouv.fr/schedules", usage: "Horaires d'activité des zones RTBA / AZBA", subs: ["4::azba", "4::zonesPRD"] },
];

// Flat list of every sub-item a reference site can be attached to (checklist fields + map items).
export const SUB_ITEMS = [
  ...CHECKLIST_POINTS.flatMap((p) =>
    (p.fields || []).map((f) => ({ id: `${p.id}::${f.key}`, label: f.label, group: `${p.id}. ${p.title}` }))
  ),
  { id: "navmap", label: "Carte de navigation", group: "Carte de Nav" },
  { id: "vac", label: "Cartes VAC", group: "Carte de Nav" },
];

export function allSites(settings) {
  return [...REFERENCE_SITES, ...((settings?.customSites) || [])];
}
// Sub-items attached to a site: user override (settings.siteSubs) otherwise the site default.
export function siteAssignedSubs(settings, siteId) {
  const ss = settings?.siteSubs?.[siteId];
  if (Array.isArray(ss)) return ss;
  return REFERENCE_SITES.find((s) => s.id === siteId)?.subs || [];
}
export function sitesForSub(settings, subId) {
  return allSites(settings).filter((s) => siteAssignedSubs(settings, s.id).includes(subId));
}
